import {
  getHealthCheckId,
  type HealthCheckConfig,
  isPageViewGroup,
} from './health-check.config';

export type OutageStatus = 'operational' | 'partial_outage' | 'major_outage';

/**
 * A day reads as a major outage once this share of its results failed —
 * a full outage lasting ~2.4 hours, or a dozen checks dead all day.
 * Below that, any failure is a partial outage.
 */
const MAJOR_OUTAGE_DAY_FAILURE_SHARE = 0.1;
/** Share of user-facing page checks red in the latest sweep that makes the board a major outage. */
const MAJOR_OUTAGE_PAGE_CHECK_SHARE = 0.25;
/** Share of all non-deprecated checks red in the latest sweep that makes the board a major outage. */
const MAJOR_OUTAGE_ALL_CHECK_SHARE = 1 / 3;

export const classifyDayOutage = (
  failureCount: number,
  totalChecks: number
): OutageStatus => {
  if (failureCount <= 0 || totalChecks <= 0) return 'operational';
  return failureCount / totalChecks >= MAJOR_OUTAGE_DAY_FAILURE_SHARE
    ? 'major_outage'
    : 'partial_outage';
};

/**
 * The board's current status from the latest result of every check. Major
 * means users are losing the product: auth is down, a quarter of the page
 * probes are red, or a third of everything non-deprecated is. Anything else
 * red — a deprecated op, an upstream, a single page — is a partial outage.
 */
export const classifySweepOutage = (
  failingCheckIds: Iterable<string>,
  checks: readonly HealthCheckConfig[]
): OutageStatus => {
  const failing = new Set(failingCheckIds);
  const isFailing = (check: HealthCheckConfig): boolean =>
    failing.has(getHealthCheckId(check.service, check.viewGroup, check.view));

  const failingChecks = checks.filter(isFailing);
  if (failingChecks.length === 0) return 'operational';
  if (failingChecks.some((check) => check.registryGroup === 'auth')) {
    return 'major_outage';
  }

  const pageChecks = checks.filter((check) => isPageViewGroup(check.viewGroup));
  const failingPages = pageChecks.filter(isFailing).length;
  if (
    pageChecks.length > 0 &&
    failingPages / pageChecks.length >= MAJOR_OUTAGE_PAGE_CHECK_SHARE
  ) {
    return 'major_outage';
  }

  const current = checks.filter((check) => !check.deprecated);
  const failingCurrent = current.filter(isFailing).length;
  if (
    current.length > 0 &&
    failingCurrent / current.length >= MAJOR_OUTAGE_ALL_CHECK_SHARE
  ) {
    return 'major_outage';
  }
  return 'partial_outage';
};
